import { useState, useRef, useCallback, useEffect } from 'react';

interface HistoryEntry<T> {
  value: T;
  cursorPosition: number | null;
}

interface UseUndoRedoProps<T> {
  initialValue: T;
  initialCursorPosition?: number | null;
}

export function useUndoRedo<T>({
  initialValue,
  initialCursorPosition = null,
}: UseUndoRedoProps<T>) {
  const [currentValue, setCurrentValue] = useState<T>(initialValue);
  const currentValueRef = useRef<T>(initialValue); // NEW: Ref for stable access in callbacks
  const currentCursorPositionRef = useRef<number | null>(initialCursorPosition);
  const undoHistoryRef = useRef<HistoryEntry<T>[]>([]);
  const redoHistoryRef = useRef<HistoryEntry<T>[]>([]);

  // Sync ref on initial state change (rare)
  useEffect(() => {
    currentValueRef.current = initialValue;
  }, [initialValue]);

  const canUndo = undoHistoryRef.current.length > 0;
  const canRedo = redoHistoryRef.current.length > 0;

  // FIXED: Use ref for "before" value; stable deps ([])
  const recordChange = useCallback((newValue: T, newCursorPosition: number | null) => {
    undoHistoryRef.current.push({
      value: currentValueRef.current, // Use ref (stable)
      cursorPosition: currentCursorPositionRef.current,
    });
    redoHistoryRef.current = [];
    setCurrentValue(newValue);
    currentValueRef.current = newValue;
    currentCursorPositionRef.current = newCursorPosition;
  }, []); // FIXED: Removed [currentValue]

  // FIXED: Use ref for "current" state; stable deps ([])
  const undo = useCallback(() => {
    if (undoHistoryRef.current.length > 0) {
      const previousEntry = undoHistoryRef.current.pop()!;
      redoHistoryRef.current.push({
        value: currentValueRef.current, // Use ref
        cursorPosition: currentCursorPositionRef.current,
      });
      setCurrentValue(previousEntry.value);
      currentValueRef.current = previousEntry.value;
      currentCursorPositionRef.current = previousEntry.cursorPosition;
      return { value: previousEntry.value, cursorPosition: previousEntry.cursorPosition };
    }
    return null;
  }, []); // FIXED: Removed [currentValue]

  // FIXED: Similar to undo
  const redo = useCallback(() => {
    if (redoHistoryRef.current.length > 0) {
      const nextEntry = redoHistoryRef.current.pop()!;
      undoHistoryRef.current.push({
        value: currentValueRef.current, // Use ref
        cursorPosition: currentCursorPositionRef.current,
      });
      setCurrentValue(nextEntry.value);
      currentValueRef.current = nextEntry.value;
      currentCursorPositionRef.current = nextEntry.cursorPosition;
      return { value: nextEntry.value, cursorPosition: nextEntry.cursorPosition };
    }
    return null;
  }, []); // FIXED: Removed [currentValue]

  const resetHistory = useCallback((newValue: T = initialValue, newCursorPosition: number | null = initialCursorPosition) => {
    undoHistoryRef.current = [];
    redoHistoryRef.current = [];
    setCurrentValue(newValue);
    currentValueRef.current = newValue; // Use ref
    currentCursorPositionRef.current = newCursorPosition;
  }, [initialValue, initialCursorPosition]);

  const updateCurrentState = useCallback((newValue: T, newCursorPosition: number | null) => {
    setCurrentValue(newValue);
    currentValueRef.current = newValue; // Use ref
    currentCursorPositionRef.current = newCursorPosition;
  }, []);

  return {
    currentValue,
    cursorPosition: currentCursorPositionRef.current,
    recordChange,
    undo,
    redo,
    resetHistory,
    canUndo,
    canRedo,
    updateCurrentState,
  };
}